import { useCallback, useEffect, useId, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { denialReason } from '@/lib/permissions';
import type { NotificationRule, ReferenceRecord } from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { NotificationRuleModal } from './NotificationRuleModal';
import { errorText, targetPath, useNotificationCatalogue } from './notification-targets';

/*
 * ROUTING RULES — `/notifications/rules` (grant `settings-notifications`).
 *
 * A rule ADDS a recipient (a staff user or an address) to every event under a
 * module, submodule or single event, for all clients or one operational region
 * (`/regions`). Replaces the retired event-interest, B2B-proposal and
 * future-billable recipient cards (their lists were migrated into rules).
 *
 * The list is re-read after every write; nothing is patched locally.
 */

function recipientName(rule: NotificationRule): string {
  if (rule.user) return rule.user.name.trim() || rule.user.email;
  return rule.email ?? '—';
}

function channelsText(rule: NotificationRule): string {
  const parts: string[] = [];
  if (rule.channelEmail) parts.push('Email');
  if (rule.channelInbox) parts.push('In-app');
  return parts.length > 0 ? parts.join(', ') : '—';
}

function scopeText(rule: NotificationRule): string {
  if (rule.scope === 'region') return rule.region?.name ?? '—';
  return 'All clients';
}

export function NotificationRulesCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const denial = denialReason(ability, 'update', 'settings-notifications');
  const canWrite = denial === null && ability.canUpdate('settings-notifications');

  const [rules, setRules] = useState<NotificationRule[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const catalogue = useNotificationCatalogue(!forbidden);
  const staff = useUserOptions(canWrite);
  const [regions, setRegions] = useState<ReferenceRecord[]>([]);

  const [editing, setEditing] = useState<NotificationRule | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [deleting, setDeleting] = useState<NotificationRule | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    return api
      .listNotificationRules(signal)
      .then((response) => {
        if (signal?.aborted) return;
        setRules(response.data);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setLoadError(caught instanceof Error ? caught.message : 'The rules could not be read.');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!canWrite) return;
    const controller = new AbortController();
    api
      .listRegions({ isActive: true, perPage: 200 }, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setRegions(response.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRegions([]);
      });
    return () => controller.abort();
  }, [canWrite]);

  function openAdd() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(rule: NotificationRule) {
    setEditing(rule);
    setModalOpen(true);
    setRowError(null);
  }

  function toggleActive(rule: NotificationRule) {
    if (togglingId !== null || !canWrite) return;
    setTogglingId(rule.id);
    setRowError(null);
    api
      .updateNotificationRule(rule.id, { isActive: !rule.isActive })
      .then(() => load())
      .catch((caught: unknown) =>
        setRowError({ id: rule.id, message: errorText(caught, 'The rule was not changed.') }),
      )
      .finally(() => setTogglingId(null));
  }

  function confirmDelete() {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    api
      .deleteNotificationRule(deleting.id)
      .then(() => {
        setDeleting(null);
        return load();
      })
      .catch((caught: unknown) => setDeleteError(errorText(caught, 'The rule was not deleted.')))
      .finally(() => setDeleteBusy(false));
  }

  const pathOf = (rule: NotificationRule) =>
    targetPath(catalogue.byId, rule.targetType, rule.targetKey);

  function activeControl(rule: NotificationRule) {
    return (
      <span title={denial ?? undefined}>
        <Checkbox
          label="Active"
          checked={rule.isActive}
          disabled={!canWrite || (togglingId !== null && togglingId !== rule.id) || togglingId === rule.id}
          onChange={() => toggleActive(rule)}
        />
      </span>
    );
  }

  function rowActions(rule: NotificationRule) {
    if (!canWrite) return null;
    return (
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => openEdit(rule)}>
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDeleteError(null);
            setDeleting(rule);
          }}
        >
          Delete
        </Button>
      </div>
    );
  }

  function recipientCell(rule: NotificationRule) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm text-fg [overflow-wrap:anywhere]">{recipientName(rule)}</span>
        {rule.user ? (
          <span className="text-xs [overflow-wrap:anywhere]">
            <EmailLink address={rule.user.email} tone="muted" />
          </span>
        ) : (
          rule.email && (
            <Badge tone="neutral" variant="outline">
              Address
            </Badge>
          )
        )}
      </div>
    );
  }

  const deletingLabel = deleting ? `${pathOf(deleting)} → ${recipientName(deleting)}` : '';

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title="Routing rules"
        actions={
          rules !== null && !forbidden ? (
            <span title={denial ?? undefined}>
              <Button size="sm" onClick={openAdd} disabled={!canWrite || catalogue.loading}>
                Add rule
              </Button>
            </span>
          ) : undefined
        }
      />
      <PanelBody className="flex flex-col gap-4">
        {loadError && (
          <Notice tone="danger" role="alert" title="Couldn’t read the routing rules.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{loadError}</span>
          </Notice>
        )}
        {catalogue.error && (
          <Notice tone="danger" role="alert" title="Couldn’t read the notification catalogue.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{catalogue.error}</span>
          </Notice>
        )}

        {loading && rules === null && !forbidden ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">
            Your permission set does not include this. An administrator can change that.
          </p>
        ) : rules === null ? null : rules.length === 0 ? (
          <EmptyState title="No routing rules" />
        ) : (
          <>
            <div className="max-md:hidden">
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                <Table>
                  <THead>
                    <TR>
                      <TH>Applies to</TH>
                      <TH>Recipient</TH>
                      <TH>Scope</TH>
                      <TH>Channels</TH>
                      <TH>Note</TH>
                      <TH>Active</TH>
                      {canWrite && (
                        <TH>
                          <span className="sr-only">Actions</span>
                        </TH>
                      )}
                    </TR>
                  </THead>
                  <TBody>
                    {rules.map((rule) => (
                      <TR key={rule.id}>
                        <TD>
                          <span className={rule.isActive ? 'text-fg' : 'text-fg-subtle'}>{pathOf(rule)}</span>
                          {rowError?.id === rule.id && (
                            <p role="alert" className="m-0 text-xs text-danger">
                              {rowError.message}
                            </p>
                          )}
                        </TD>
                        <TD>{recipientCell(rule)}</TD>
                        <TD>{scopeText(rule)}</TD>
                        <TD>{channelsText(rule)}</TD>
                        <TD className="max-w-60 [overflow-wrap:anywhere]">{rule.note ?? '—'}</TD>
                        <TD>{activeControl(rule)}</TD>
                        {canWrite && <TD>{rowActions(rule)}</TD>}
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </div>

            <ul className="m-0 flex list-none flex-col divide-y divide-border p-0 md:hidden">
              {rules.map((rule) => (
                <li key={rule.id} className="flex flex-col gap-2 py-3">
                  <span
                    className={
                      rule.isActive ? 'text-sm font-medium text-fg' : 'text-sm font-medium text-fg-subtle'
                    }
                  >
                    {pathOf(rule)}
                  </span>
                  {recipientCell(rule)}
                  <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                    <dt className="text-fg-subtle">Scope</dt>
                    <dd className="m-0 text-fg-secondary">{scopeText(rule)}</dd>
                    <dt className="text-fg-subtle">Channels</dt>
                    <dd className="m-0 text-fg-secondary">{channelsText(rule)}</dd>
                    <dt className="text-fg-subtle">Note</dt>
                    <dd className="m-0 text-fg-secondary [overflow-wrap:anywhere]">{rule.note ?? '—'}</dd>
                  </dl>
                  {rowError?.id === rule.id && (
                    <p role="alert" className="m-0 text-xs text-danger">
                      {rowError.message}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {activeControl(rule)}
                    {rowActions(rule)}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </PanelBody>

      <NotificationRuleModal
        open={modalOpen}
        rule={editing}
        targets={catalogue.targets}
        staff={staff}
        regions={regions}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          void load();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        onConfirm={confirmDelete}
        title="Delete this routing rule?"
        description={
          deleting
            ? `${deletingLabel}. ${recipientName(deleting)} stops receiving these notifications through this rule. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleteBusy}
        error={deleteError ?? undefined}
      />
    </Panel>
  );
}
